import { OHIF, PredictionAdmin, TaskAdmin } from '@gleamer/types';
import { failure } from './taskAdmin';

type AuthHeaderSource = Pick<OHIF.UserAuthenticationService, 'getAuthorizationHeader'>;

type PredictionProduct = PredictionAdmin.PredictionProduct;
type NewPredictionProduct = PredictionAdmin.NewPredictionProduct;
type PredictionProductChange = PredictionAdmin.PredictionProductChange;

type PredictionUpload = PredictionAdmin.PredictionUpload;
type PredictionFileImportRequest = PredictionAdmin.PredictionFileImportRequest;
type PredictionImport = TaskAdmin.PredictionImport;

const PRODUCTS = '/api/admin/prediction-products';
const UPLOADS = '/api/admin/prediction-uploads';
const IMPORTS = '/api/prediction-imports';
const FILES = '/api/admin/prediction-files';

async function send<T>(
  auth: AuthHeaderSource,
  url: string,
  method: string,
  body?: unknown
): Promise<T> {
  const headers = new Headers(auth.getAuthorizationHeader());
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw await failure(response);
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

/** Every product, by name (read:prediction). */
export function fetchPredictionProducts(auth: AuthHeaderSource): Promise<PredictionProduct[]> {
  return send(auth, PRODUCTS, 'GET');
}

/** Creates a product (create:prediction); 409 when its code is taken. */
export function createPredictionProduct(
  auth: AuthHeaderSource,
  product: NewPredictionProduct
): Promise<PredictionProduct> {
  return send(auth, PRODUCTS, 'POST', product);
}

/** Renames or describes a product (create:prediction); its code stays. */
export function updatePredictionProduct(
  auth: AuthHeaderSource,
  productId: number,
  change: PredictionProductChange
): Promise<PredictionProduct> {
  return send(auth, `${PRODUCTS}/${encodeURIComponent(String(productId))}`, 'PUT', change);
}

/**
 * Deletes a product with its empty imports (create:prediction); 409, with the reason, while it holds predictions
 * or an import of it is in flight.
 */
export function deletePredictionProduct(auth: AuthHeaderSource, productId: number): Promise<void> {
  return send(auth, `${PRODUCTS}/${encodeURIComponent(String(productId))}`, 'DELETE');
}

/** Where to upload a file of that name (create:prediction): a signed URL, valid for a few minutes. */
export function requestPredictionUpload(
  auth: AuthHeaderSource,
  fileName: string
): Promise<PredictionUpload> {
  return send(auth, UPLOADS, 'POST', { fileName });
}

/**
 * The file PUT to the signed URL, straight to Cloud Storage: the URL is the authorization, so no Authorization
 * header goes with it.
 */
export async function uploadPredictionFile(upload: PredictionUpload, file: Blob): Promise<void> {
  const response = await fetch(upload.url, { method: 'PUT', body: file });
  if (!response.ok) {
    throw new Error(`The upload was refused (${response.status} ${response.statusText})`.trim());
  }
}

/** The uploaded file's import into a product (create:prediction); 409 while one of the product's is in flight. */
export function importPredictionFile(
  auth: AuthHeaderSource,
  request: PredictionFileImportRequest
): Promise<PredictionImport> {
  return send(auth, IMPORTS, 'POST', request);
}

/** The file imports, newest first; one product's with `productId` (read:prediction). */
export function fetchPredictionFileImports(
  auth: AuthHeaderSource,
  { productId, limit, offset }: { productId?: number; limit: number; offset: number }
): Promise<PredictionImport[]> {
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (productId !== undefined) {
    query.set('productId', String(productId));
  }
  return send(auth, `${IMPORTS}?${query}`, 'GET');
}

/** The files dropped in the predictions bucket, newest first (read:prediction). */
export function fetchPredictionFiles(
  auth: AuthHeaderSource,
  { limit, offset }: { limit: number; offset: number }
): Promise<PredictionAdmin.PredictionFile[]> {
  return send(auth, `${FILES}?${new URLSearchParams({ limit: String(limit), offset: String(offset) })}`, 'GET');
}

/** A page of an import's patients with their counts, and how many it holds (read:prediction). */
export function fetchPredictionImportPatients(
  auth: AuthHeaderSource,
  importId: number,
  { limit, offset }: { limit: number; offset: number }
): Promise<PredictionAdmin.PredictionImportPatients> {
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return send(auth, `${IMPORTS}/${encodeURIComponent(String(importId))}/patients?${query}`, 'GET');
}

/** One patient's studies, series and images in an import (read:prediction). */
export function fetchPredictionImportStudies(
  auth: AuthHeaderSource,
  importId: number,
  patientId: string
): Promise<PredictionAdmin.PredictionImportStudy[]> {
  return send(
    auth,
    `${IMPORTS}/${encodeURIComponent(String(importId))}/patients/${encodeURIComponent(patientId)}`,
    'GET'
  );
}

/** Moves a DONE file import to another product; its BigQuery table is renamed (create:prediction). 409 when it cannot. */
export function changePredictionImportProduct(
  auth: AuthHeaderSource,
  importId: number,
  productId: number
): Promise<PredictionImport> {
  return send(auth, `${IMPORTS}/${encodeURIComponent(String(importId))}/product`, 'POST', { productId });
}

/** Deletes an import, its predictions, issues and BigQuery table (create:prediction). 409 while it is IMPORTING. */
export function deletePredictionImport(auth: AuthHeaderSource, importId: number): Promise<void> {
  return send(auth, `${IMPORTS}/${encodeURIComponent(String(importId))}`, 'DELETE');
}

